import React from 'react';
import { motion, Variants } from 'framer-motion';
import { FaShieldAlt, FaPhoneSlash, FaBullseye, FaRobot, FaCommentAlt, FaCheckCircle, FaUsers, FaDatabase, FaHandshake, FaMoneyBillWave, FaDownload, FaGavel, FaBullhorn } from 'react-icons/fa';

// Placeholder Images - you should replace these with your own assets
import heroImage from '../../assets/Scam/scamhero.jpg';
import featureImage1 from '../../assets/features/spam-caller.png'; // Using this image for the visual showcase


// Framer Motion Variants for animations
const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2,
            delayChildren: 0.1
        }
    }
};

const textVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const imageVariants: Variants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: "easeOut" } }
};

const cardVariants: Variants = {
    hidden: { opacity: 0, y: 20, scale: 0.9 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: "easeOut" } }
};

const ScamAlertPage = () => {
    const features = [
        {
            icon: FaBullseye,
            title: "Real-Time Accuracy",
            description: "Our proprietary algorithm instantly identifies and flags suspicious callers, providing you with a clear warning before you even answer."
        },
        {
            icon: FaPhoneSlash,
            title: "Auto-Block Known Scams",
            description: "Automatically block calls from a constantly updated database of fraudulent numbers, ensuring your phone stays silent from spam."
        },
        {
            icon: FaRobot,
            title: "AI-Powered Threat Detection",
            description: "We use advanced AI to analyze new and emerging scam patterns, keeping you one step ahead of the latest threats."
        },
        {
            icon: FaCheckCircle,
            title: "Community-Verified Data",
            description: "Join our network of users who report scams in real-time, helping to protect everyone in the COC community."
        },
    ];


    const blogPosts = [
        {
            image: "https://www.scamwatcher.com/images/open_graph/og3.jpg",
            title: "The Top 5 Scams of 2024 to Watch Out For",
            date: "May 25, 2024",
            readTime: "5 min read"
        },
        {
            image: "https://images.saymedia-content.com/.image/t_share/MTc0NDY3NzkzNjI4OTY0MjAw/deconstructing-an-internet-scam-on-line-criminals-only-win-when-you-let-them.jpg",
            title: "AI in Spam Detection: How We're Fighting Back",
            date: "May 18, 2024",
            readTime: "7 min read"
        },
        {
            image: "https://www.rd.com/wp-content/uploads/2021/02/RD-15_phone-call-scams_GettyImages-1351333649.jpg",
            title: "How to Keep Your Loved Ones Safe from Fraud",
            date: "May 10, 2024",
            readTime: "4 min read"
        },
        {
            image: "https://www.scam-detector.com/wp-content/uploads/2020/09/phone-scams.jpeg",
            title: "The Latest in Vishing and Smishing Attacks",
            date: "May 3, 2024",
            readTime: "6 min read"
        }
    ];

    const scamData = [
  { title: "Family Emergency Scams", image: "https://static.vecteezy.com/system/resources/previews/047/449/205/non_2x/fraud-scammer-isometric-illustration-vector.jpg" },
  { title: "Social Engineering Scams", image: "https://img.pimeyes.com/be-aware-of-scammers_G5NGBE3AJ.jpg" },
  { title: "Cryptocurrency Scams", image: "https://img.freepik.com/premium-photo/scammer-stares-monitor-plotting-with-smartphone-they-hunt-victims-their-schemes_733217-3502.jpg" },
  { title: "Grandparent Scams", image: "https://b1681952.smushcdn.com/1681952/wp-content/uploads/2022/12/scam-likely-phone-call-sz-970x546.jpg?lossy=0&strip=1&webp=1" },
  { title: "Pretexting Scams", image: "https://www.stcharleshealthcare.org/sites/default/files/Featured%20Images/Blog/phone-scammer-blog_0.jpg" },
];

    return (
        <div className=" text-gray-800 overflow-hidden">
            {/* Hero Section */}
            <section className="relative h-[650px] flex flex-col md:flex-row items-center justify-center bg-gray-900 text-white overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-700 to-green-600 to-black opacity-90"></div>
                
                <motion.div
                    className="container mx-auto px-6 relative z-10 flex flex-col md:flex-row items-center justify-between py-16 md:py-0"
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                >
                    <div className="md:w-1/2 text-center md:text-left mb-12 md:mb-0">
                        <motion.h1
                            className="text-4xl md:text-6xl font-extrabold leading-tight mb-4 text-[#AEEA00]"
                            variants={textVariants}
                        >
                            COC Scam Alert : <br />
                            <span className="text-gray-300">Your Ultimate Guardian</span>
                        </motion.h1>
                        <motion.p
                            className="text-lg md:text-xl text-gray-300 max-w-lg mb-8 mx-auto md:mx-0"
                            variants={textVariants}
                        >
                            Unmask fraudulent callers and protect your peace of mind with COC’s powerful, AI-driven Scam Alert system.
                        </motion.p>
                        <motion.a
                            href="/download"
                            className="inline-block bg-[#AEEA00] text-[#1A237E] py-4 px-10 rounded-full font-bold shadow-lg transition-colors duration-300 transform hover:scale-105"
                            variants={textVariants}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Protect Your Phone Now
                        </motion.a>
                    </div>
                    
                    <motion.div
                        className="md:w-1/2 flex justify-center items-center"
                        initial="hidden"
                        animate="visible"
                        variants={imageVariants}
                    >
                        <img
                            src={featureImage1}
                            alt="Scam Alert on phone"
                            className="w-full max-w-[300px] h-auto md:max-w-[400px] rounded-3xl shadow-2xl mt-10"
                        />
                    </motion.div>
                </motion.div>
            </section>

            {/* Core Features Section */}
            <section className="py-20 md:py-24">
                <div className="container mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-[#1A237E] mb-12">
                        How COC Keeps You Safe
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-2"
                                variants={cardVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                            >
                                <div className="text-center text-[#AEEA00] mb-4">
                                    <feature.icon className="text-5xl mx-auto" />
                                </div>
                                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                                <p className="text-sm text-gray-600">{feature.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

           <section className="py-16">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-[#1A237E] mb-10 text-center">
          Scams Around the World
        </h2>
        <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {scamData.map((scam, index) => (
            <div
              key={index}
              className="bg-gray-100 hover:shadow-xl transition rounded-xl overflow-hidden text-center"
            >
              <img
                src={scam.image}
                alt={scam.title}
                className="w-full h-40 object-cover"
              />
              <div className="p-4">
                <p className="text-sm text-gray-500 mb-1">🌐 Global</p>
                <h3 className="text-lg font-semibold text-[#1A237E]">{scam.title}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

            {/* New Section: What to do if you have been a victim of a scam? */}
            <section className="py-20 md:py-24 ">
                <div className="container mx-auto px-6">
                    <h2 className="text-3xl md:text-4xl font-bold text-[#1A237E] mb-12 text-center">
                        What to do if you have been a victim of a scam?
                    </h2>
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <motion.div
                            className="relative flex justify-center items-center"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.5 }}
                            variants={imageVariants}
                        >
                            <img
                                src={"https://www.rd.com/wp-content/uploads/2021/06/Facebook-Marketplace-Scams-Collage01-700x467.jpg"}
                                alt="Scam victim protection"
                                className="w-full max-w-[500px] h-auto rounded-2xl shadow-2xl"
                            />
                        </motion.div>
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.5 }}
                            variants={textVariants}
                        >
                            <ul className="space-y-6">
                                <li className="flex items-start">
                                    <div className="flex-shrink-0 text-[#AEEA00] mt-1">
                                        <FaGavel className="text-2xl" />
                                    </div>
                                    <div className="ml-4">
                                        <p className="text-lg text-gray-700">
                                            Report to the local authority about the scam.
                                        </p>
                                    </div>
                                </li>
                                <li className="flex items-start">
                                    <div className="flex-shrink-0 text-[#AEEA00] mt-1">
                                        <FaMoneyBillWave className="text-2xl" />
                                    </div>
                                    <div className="ml-4">
                                        <p className="text-lg text-gray-700">
                                            If there is a banking activity involved, contact your bank immediately to stop the transactions.
                                        </p>
                                    </div>
                                </li>
                                <li className="flex items-start">
                                    <div className="flex-shrink-0 text-[#AEEA00] mt-1">
                                        <FaDownload className="text-2xl" />
                                    </div>
                                    <div className="ml-4">
                                        <p className="text-lg text-gray-700">
                                            Download the COC app if not downloaded already, and stay protected from these frauds with the help of our Caller ID, Message ID, and spam blocking features.
                                        </p>
                                    </div>
                                </li>
                            </ul>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* New Section: How to protect yourself from different scams? */}
            <section className="py-20 md:py-24 ">
                <div className="container mx-auto px-6">
                    <h2 className="text-3xl md:text-4xl font-bold text-[#1A237E] mb-12 text-center">
                        How to protect yourself from different scams?
                    </h2>
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.5 }}
                            variants={textVariants}
                        >
                            <ul className="space-y-6">
                                <li className="flex items-start">
                                    <div className="flex-shrink-0 text-[#AEEA00] mt-1">
                                        <FaHandshake className="text-2xl" />
                                    </div>
                                    <div className="ml-4">
                                        <p className="text-lg text-gray-700">
                                            Ensure that you report these scams to government bodies that help in fighting these scams and protecting the consumers.
                                        </p>
                                    </div>
                                </li>
                                <li className="flex items-start">
                                    <div className="flex-shrink-0 text-[#AEEA00] mt-1">
                                        <FaBullhorn className="text-2xl" />
                                    </div>
                                    <div className="ml-4">
                                        <p className="text-lg text-gray-700">
                                            Ensure you educate the people around you by posting the incidents in your family groups and on social media!
                                        </p>
                                    </div>
                                </li>
                                <li className="flex items-start">
                                    <div className="flex-shrink-0 text-[#AEEA00] mt-1">
                                        <FaShieldAlt className="text-2xl" />
                                    </div>
                                    <div className="ml-4">
                                        <p className="text-lg text-gray-700">
                                            If you are a COC user, report these phone numbers so that the rest of the COC community is also protected from these scammers.
                                        </p>
                                    </div>
                                </li>
                            </ul>
                        </motion.div>
                        <motion.div
                            className="relative flex justify-center items-center"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.5 }}
                            variants={imageVariants}
                        >
                            <img
                                src={"https://www.utma.com/wp-content/uploads/2020/05/Avoid-scams.jpg"}
                                alt="Scam protection"
                                className="w-full max-w-[500px] h-auto rounded-2xl shadow-2xl"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* New Section: From the COC Blog */}
            <section className="py-20 md:py-24 ">
                <div className="container mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-[#1A237E] mb-12">
                        From the COC Blog
                    </h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
                        {blogPosts.map((post, index) => (
                            <motion.div
                                key={index}
                                className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 transform hover:scale-105"
                                variants={cardVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <img
                                    src={post.image}
                                    alt={post.title}
                                    className="w-full h-48 object-cover"
                                />
                                <div className="p-6 text-left">
                                    <h3 className="text-lg font-bold text-[#1A237E] mb-2 leading-snug">
                                        {post.title}
                                    </h3>
                                    <p className="text-sm text-gray-500">{post.date} • {post.readTime}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ScamAlertPage;